import Icon from '@/components/ui/AppIcon';
import React from 'react';

// Product image crops from the brochure image
// The brochure image contains 4 products: Limetron (top-left), Magcat (top-right), Minitron (bottom-left), Showercat (bottom-right)
const productImageCrops: Record<string, { objectPosition: string; src?: string }> = {
  limetron: { objectPosition: '50% 50%', src: '/assets/images/Ontwerp_20zonder_20titel-1772989013752.png' },
  magcat: { objectPosition: '50% 50%', src: '/assets/images/Untitled_20design-1772989916811.png' },
  minitron: { objectPosition: '50% 50%', src: '/assets/images/Ontwerp_20zonder_20titel_202-1772990071386.png' },
  showercat: { objectPosition: '50% 50%', src: '/assets/images/Untitled_20design_203-1772990185130.png' },
};

interface Product {
  id: string;
  name: string;
  tagline: string;
  description: string;
  geschiktVoor: string[];
  voordelen: string[];
  accentColor: string;
  icon: string;
  startingPrice: string;
  originalPrice: string | null;
}

const products: Product[] = [
  {
    id: 'limetron',
    name: 'FrenaCal FD Hogar',
    tagline: 'Hele woning oplossing',
    description:
      'FrenaCal FD Hogar is ontwikkeld voor particuliere huishoudens die kalkproblemen effectief willen aanpakken zonder zout, chemicaliën of stroom. Het systeem is geschikt voor 90% van drinkwaterbronnen en heeft een levensduur van 15–25+ jaar.',
    geschiktVoor: [
      'Complete woninginstallatie',
      'CV-ketel en boilers',
      'Keukenapparatuur',
      'Douche- en badkamersystemen',
    ],
    voordelen: [
      'Geen zout, stroom of chemicaliën',
      'Milieuvriendelijk',
      'Laag onderhoud',
      'Wetenschappelijk gevalideerd',
    ],
    accentColor: '#0584F2',
    icon: 'HomeIcon',
    startingPrice: '€1.450',
    originalPrice: '€1.700',
  },
  {
    id: 'magcat',
    name: 'FrenaCal FD Commercio',
    tagline: 'Dubbele behandeling',
    description:
      'FrenaCal FD Commercio is het meest geavanceerde systeem binnen het FrenaCal-assortiment, met een combinatie van katalytische én magnetische behandeling voor extreem hard water met hoge calcium- en magnesiumgehaltes.',
    geschiktVoor: [
      'Appartementen & industrie',
      'Zakelijk gebruik',
      'Industriële wasmachines',
      'Stoomketels',
    ],
    voordelen: [
      'Dubbele behandeling',
      'Geschikt voor hoog-calcium water',
      'Energiebesparing',
      'Langdurig effect',
    ],
    accentColor: '#33BEBE',
    icon: 'BuildingOfficeIcon',
    startingPrice: '€1.900',
    originalPrice: '€2.150',
  },
  {
    id: 'minitron',
    name: 'FrenaCal FD Mini',
    tagline: 'Compact systeem',
    description:
      'FrenaCal FD Mini is speciaal ontwikkeld voor lage doorstroming en specifieke apparaten. Dit compacte systeem is ideaal voor toepassingen waar kleine directe aansluiting wenselijk is, zoals koffiezetapparaten en vaatwassers.',
    geschiktVoor: [
      'Koffiezetapparaten',
      'Vaatwassers & ijsmachines',
      'Waterkokers',
      'Kleine pompsystemen',
    ],
    voordelen: [
      'Compact formaat',
      'Eenvoudige installatie',
      'Geen stroom nodig',
      'Verlengde levensduur apparaten',
    ],
    accentColor: '#0584F2',
    icon: 'WrenchScrewdriverIcon',
    startingPrice: '€215',
    originalPrice: null,
  },
  {
    id: 'showercat',
    name: 'FrenaCal FD Micro',
    tagline: 'Specifiek voor douches',
    description:
      'FrenaCal FD Micro is ontwikkeld om kalkafzetting in elektrische douches en douchekoppen te voorkomen. Het systeem werkt direct in de watertoevoer en zorgt voor optimale waterdruk en een langere levensduur van uw doucheapparatuur.',
    geschiktVoor: [
      'Elektrische douches',
      'Douchekoppen',
      'Badkamerkranen',
    ],
    voordelen: [
      'Minder kalkaanslag op douche',
      'Betere waterdruk',
      'Eenvoudig te installeren',
      'Geen stroom nodig',
    ],
    accentColor: '#33BEBE',
    icon: 'SparklesIcon',
    startingPrice: '€215',
    originalPrice: null,
  },
];

export default function ProductenSection() {
  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="producten"
      className="py-20 md:py-32 relative overflow-hidden"
    >
      {/* Background glow elements — softened for light bg */}
      <div
        className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(5,132,242,0.06) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(51,190,190,0.06) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="mx-auto max-w-7xl px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-8 reveal">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border"
            style={{ borderColor: 'rgba(5,132,242,0.3)', background: 'rgba(5,132,242,0.08)' }}
          >
            <Icon name="CubeIcon" size={16} style={{ color: '#0584F2' }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#0584F2' }}>
              Productlijn
            </span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-extrabold"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              background: 'linear-gradient(135deg, #0F1E2E 0%, #0584F2 50%, #33BEBE 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Onze Producten
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#2F4F6E' }}>
            Vier gespecialiseerde oplossingen voor elk type kalkprobleem — van complete
            woninginstallaties tot compacte apparaatbescherming.
          </p>
        </div>

        {/* Early-Bird Discount Banner */}
        <div className="mb-12 reveal">
          <div
            className="relative rounded-2xl overflow-hidden px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{
              background: 'linear-gradient(135deg, #FF6B00 0%, #FF8C00 50%, #FFB300 100%)',
              boxShadow: '0 8px 32px -4px rgba(255,107,0,0.40)',
            }}
          >
            {/* Animated pulse ring */}
            <div className="absolute -top-3 -left-3 w-16 h-16 rounded-full bg-white/10 animate-ping pointer-events-none" />
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Icon name="TagIcon" size={24} style={{ color: '#fff' }} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-black text-lg md:text-xl leading-tight">
                    🎉 Tijdelijk vroegboekerskorting: <span className="underline decoration-wavy">− €250</span>
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/25 text-white text-xs font-bold uppercase tracking-wider">
                    Beperkt beschikbaar
                  </span>
                </div>
                <p className="text-white/90 text-sm mt-0.5">
                  Profiteer nu van €250 korting als eerste koper — deze actie is tijdelijk en kan elk moment stoppen.
                </p>
              </div>
            </div>
            <button
              onClick={scrollToContact}
              className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-black whitespace-nowrap hover:scale-105 transition-transform"
              style={{ color: '#FF6B00' }}
            >
              <Icon name="ArrowRightIcon" size={16} style={{ color: '#FF6B00' }} />
              Claim mijn korting
            </button>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product, index) => {
            const crop = productImageCrops[product.id];
            return (
              <div
                key={product.id}
                className="card-3d rounded-2xl overflow-visible reveal"
                style={{
                  background: '#FFFFFF',
                  border: `1px solid ${product.accentColor}30`,
                  boxShadow: `0 4px 24px -4px ${product.accentColor}20`,
                  animationDelay: `${index * 0.15}s`,
                }}
              >
                {/* Product Photo */}
                <div
                  className="relative flex items-center justify-center rounded-t-2xl overflow-hidden"
                  style={{
                    height: '160px',
                    background: `linear-gradient(160deg, #F0F8FF 0%, #EBF5FF 50%, ${product.accentColor}10 100%)`,
                    borderBottom: `1px solid ${product.accentColor}20`,
                  }}
                >
                  {/* Subtle radial glow behind image */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background: `radial-gradient(ellipse at center, ${product.accentColor}12 0%, transparent 70%)`,
                    }}
                  />
                  <img
                    src={crop.src ?? '/assets/images/Untitled-1772977162324.jpeg'}
                    alt={`${product.name} product foto`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: crop.objectPosition,
                      maxHeight: '160px',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  />
                </div>

                {/* Card Header */}
                <div
                  className="p-6 pb-4"
                  style={{
                    background: `linear-gradient(135deg, ${product.accentColor}08 0%, transparent 100%)`,
                    borderBottom: `1px solid ${product.accentColor}15`,
                  }}
                >
                  <div className="flex items-start gap-4">
                    {/* Product Icon */}
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${product.accentColor}20 0%, ${product.accentColor}08 100%)`,
                        border: `1px solid ${product.accentColor}30`,
                      }}
                    >
                      <Icon name={product.icon} size={22} style={{ color: product.accentColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-xs font-bold uppercase tracking-widest mb-1"
                        style={{ color: product.accentColor }}
                      >
                        {product.tagline}
                      </div>
                      <h3
                        className="text-xl font-extrabold"
                        style={{ fontFamily: 'Montserrat, sans-serif', color: '#0F1E2E' }}
                      >
                        {product.name}
                      </h3>
                    </div>
                    {/* Starting Price Badge */}
                    <div
                      className="flex-shrink-0 flex flex-col items-end justify-center"
                    >
                      {product.originalPrice ? (
                        <div
                          className="rounded-xl px-3 py-2 text-center relative"
                          style={{
                            background: 'linear-gradient(135deg, #FF6B0018 0%, #FF8C0010 100%)',
                            border: '1.5px solid #FF6B0040',
                          }}
                        >
                          {/* Discount badge */}
                          <div
                            className="absolute -top-2.5 -right-2.5 rounded-full px-1.5 py-0.5 text-[9px] font-black text-white leading-none"
                            style={{ background: '#FF6B00' }}
                          >
                            −€250
                          </div>
                          <div
                            className="text-[10px] font-bold uppercase tracking-widest leading-none mb-0.5"
                            style={{ color: '#FF6B00', opacity: 0.85 }}
                          >
                            vroegboeker
                          </div>
                          <div
                            className="text-[11px] leading-none line-through mb-0.5"
                            style={{ color: '#9CA3AF' }}
                          >
                            {product.originalPrice}
                          </div>
                          <div
                            className="text-lg font-extrabold leading-none"
                            style={{ fontFamily: 'Montserrat, sans-serif', color: '#FF6B00' }}
                          >
                            {product.startingPrice}
                          </div>
                          <div
                            className="text-[9px] font-bold leading-none mt-0.5"
                            style={{ color: '#FF6B00', opacity: 0.7 }}
                          >
                            vanaf
                          </div>
                        </div>
                      ) : (
                        <div
                          className="rounded-xl px-3 py-2 text-center"
                          style={{
                            background: `linear-gradient(135deg, ${product.accentColor}18 0%, ${product.accentColor}08 100%)`,
                            border: `1.5px solid ${product.accentColor}40`,
                          }}
                        >
                          <div
                            className="text-[10px] font-bold uppercase tracking-widest leading-none mb-1"
                            style={{ color: product.accentColor, opacity: 0.8 }}
                          >
                            vanaf
                          </div>
                          <div
                            className="text-lg font-extrabold leading-none"
                            style={{ fontFamily: 'Montserrat, sans-serif', color: product.accentColor }}
                          >
                            {product.startingPrice}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 space-y-5">
                  <p className="text-sm leading-relaxed" style={{ color: '#2F4F6E' }}>{product.description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Geschikt voor */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-1.5 h-4 rounded-full"
                          style={{ background: product.accentColor }}
                        />
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#2F4F6E' }}>
                          Geschikt voor
                        </span>
                      </div>
                      <ul className="space-y-1.5">
                        {product.geschiktVoor.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <span
                              className="mt-1 flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                              style={{ background: `${product.accentColor}20`, border: `1px solid ${product.accentColor}40` }}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: product.accentColor }}
                              />
                            </span>
                            <span className="text-sm" style={{ color: '#2F4F6E' }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Voordelen */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div
                          className="w-1.5 h-4 rounded-full"
                          style={{ background: '#0584F2' }}
                        />
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#2F4F6E' }}>
                          Voordelen
                        </span>
                      </div>
                      <ul className="space-y-1.5">
                        {product.voordelen.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <Icon
                              name="CheckCircleIcon"
                              size={16}
                              className="mt-0.5 flex-shrink-0"
                              style={{ color: '#0584F2' }}
                            />
                            <span className="text-sm" style={{ color: '#2F4F6E' }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={scrollToContact}
                    className="w-full mt-2 py-3 px-5 rounded-xl text-sm font-bold text-white transition-all duration-300 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                    style={{
                      background: `linear-gradient(135deg, ${product.accentColor} 0%, ${product.accentColor === '#0584F2' ? '#33BEBE' : '#0584F2'} 100%)`,
                      boxShadow: `0 4px 20px -4px ${product.accentColor}40`,
                    }}
                  >
                    Meer informatie over {product.name.split(' ').slice(1).join(' ')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
